import axios from 'axios';
import { setAlert } from './alert';

const API_URL = process.env.REACT_APP_SERVER_API_URL;

const ACCESS_URL = `${API_URL}/whatsapp-access`;

const request = ({ method, path, data, fallback, success, pick = (body) => body.data }) => async dispatch => {
    try {
        const res = await axios({ method, url: `${ACCESS_URL}${path}`, data });
        if (res.data.success) {
            if (success) dispatch(setAlert(success, 'success'));
            return pick(res.data);
        }
        dispatch(setAlert(res.data.error || fallback, 'danger'));
        return null;
    } catch (err) {
        console.error(fallback, err);
        dispatch(setAlert(err.response?.data?.error || fallback, 'danger'));
        return null;
    }
};

/**
 * Customers allowed to receive WhatsApp flows; an empty list means nobody receives them
 * @returns {Promise<Array<{phone: string, name: string, userId: string|null}>|null>} null after an alert on failure
 */
export const getWhatsappAllowlist = () => request({
    method: 'get',
    path: '/allowlist',
    fallback: 'Failed to load the WhatsApp allowlist'
});

/**
 * Allow one customer to receive WhatsApp flows
 * @param {string} userId - Customer id
 * @returns {Promise<Object|null>} the allowed customer, or null after an alert on failure
 */
export const allowWhatsappCustomer = (userId) => request({
    method: 'post',
    path: '/allowlist',
    data: { userId },
    fallback: 'Failed to allow the customer',
    success: 'Customer added to the WhatsApp allowlist'
});

/**
 * Stop one customer from receiving WhatsApp flows
 * @param {string} phone - 10 digit customer phone
 * @returns {Promise<Object|null>} null after an alert on failure
 */
export const removeWhatsappCustomer = (phone) => request({
    method: 'delete',
    path: `/allowlist/${phone}`,
    fallback: 'Failed to remove the customer',
    success: 'Customer removed from the WhatsApp allowlist'
});

/**
 * Staff numbers that receive internal WhatsApp updates, with the alert kinds an admin can route
 * @returns {Promise<{staff: Array<{phone: string, name: string, alerts: string[]}>, alertOptions: Object<string, string>}|null>} null after an alert on failure
 */
export const getWhatsappStaff = () => request({
    method: 'get',
    path: '/staff',
    fallback: 'Failed to load the staff numbers',
    pick: (body) => ({ staff: body.data, alertOptions: body.alertOptions || {} })
});

/**
 * Choose which internal alerts one staff number receives; an empty list means all of them
 * @param {string} phone - 10 digit phone
 * @param {string[]} alerts - Alert keys from alertOptions
 * @returns {Promise<{phone: string, alerts: string[]}|null>} null after an alert on failure
 */
export const updateWhatsappStaffAlerts = (phone, alerts) => request({
    method: 'patch',
    path: `/staff/${phone}`,
    data: { alerts },
    fallback: 'Failed to save the alerts'
});

/**
 * Which alert group (support or sales) each customer request type notifies
 * @returns {Promise<{routing: Object<string, string>, requestTypes: Object<string, string>, alertOptions: Object<string, string>}|null>} null after an alert on failure
 */
export const getWhatsappSupportRouting = () => request({
    method: 'get',
    path: '/support-routing',
    fallback: 'Failed to load the request routing',
    pick: (body) => ({ routing: body.data, requestTypes: body.requestTypes || {}, alertOptions: body.alertOptions || {} })
});

/**
 * Choose the alert group for one or more request types
 * @param {Object<string, string>} routing - Request type to alert key, for example { talk_to_support: 'sales_request' }
 * @returns {Promise<{routing: Object<string, string>}|null>} the full routing after the change, or null after an alert on failure
 */
export const updateWhatsappSupportRouting = (routing) => request({
    method: 'put',
    path: '/support-routing',
    data: { routing },
    fallback: 'Failed to save the request routing',
    success: 'Request routing saved',
    pick: (body) => ({ routing: body.data })
});

/**
 * Add a staff number to the internal WhatsApp updates
 * @param {Object} payload
 * @param {string} payload.phone - 10 digit phone
 * @param {string} [payload.name] - Label shown in the list, up to 60 characters
 * @returns {Promise<Object|null>} the new recipient, or null after an alert on failure
 */
export const addWhatsappStaff = ({ phone, name }) => request({
    method: 'post',
    path: '/staff',
    data: { phone, name },
    fallback: 'Failed to add the staff number',
    success: 'Staff number added'
});

/**
 * Remove a staff number from the internal WhatsApp updates
 * @param {string} phone - 10 digit phone
 * @returns {Promise<Object|null>} null after an alert on failure
 */
export const removeWhatsappStaff = (phone) => request({
    method: 'delete',
    path: `/staff/${phone}`,
    fallback: 'Failed to remove the staff number',
    success: 'Staff number removed'
});
